import { Building2, Handshake, type LucideIcon } from 'lucide-react'
import { useCallback } from 'react'
import { CompanySwitcher } from './CompanySwitcher'
import { useAppAuth } from '@/contexts/app-auth.context'
import { companiesClient } from '@/lib/api'
import type { CompanyResponse } from '@super/shared-types'
import { formatUSPhoneForDisplay } from '@super/shared-types'

const TWENTY_ICONS_BASE_URL = 'https://twenty-icons.com'

type Company = {
  id?: string
  name: string
  logo: LucideIcon | string
  plan: string
  subtitle?: string
}

export function SuperAdminCompanySwitcher() {
  const { switchSuperAdminCompany, currentCompany, currentCompanyPartner } =
    useAppAuth()

  // Fetch companies function for super admin
  const fetchCompanies = useCallback(
    async (query?: string): Promise<Company[]> => {
      try {
        // Use the companies API client to search for companies
        const response = await companiesClient.searchCompanies({
          q: query || '',
          limit: 10, // Reasonable limit for the dropdown
        })

        // Transform the API response to match the Company type expected by CompanySwitcher
        const companies = response.companies || []
        return companies.map((company: CompanyResponse) => {
          const domainUrl = company.domainName

          // Use Handshake icon for partner companies
          let logo: LucideIcon | string = Building2
          if (company.isPartner) {
            logo = Handshake
          } else if (domainUrl) {
            logo = `${TWENTY_ICONS_BASE_URL}/${domainUrl}`
          }

          return {
            id: company.id,
            name: company.name,
            logo,
            plan: 'Company',
            subtitle: company.isPartner
              ? 'Partner'
              : company.phone
                ? formatUSPhoneForDisplay(company.phone)
                : domainUrl,
          }
        })
      } catch (error) {
        console.error('Error fetching companies:', error)
        return []
      }
    },
    [],
  )

  /**
   * Handle company selection
   * @param company - The company to switch to
   */
  const handleCompanySelect = async (company: Company) => {
    if (!company.id) {
      console.error('Company ID is required for switching')
      return
    }

    try {
      await switchSuperAdminCompany(company.id)
    } catch (error) {
      console.error('Error switching company:', error)
    }
  }

  const selectedCompany = currentCompany
    ? {
        id: currentCompany.id,
        name: currentCompany.name,
        logo: (() => {
          // Use Handshake icon for partner companies
          if (currentCompanyPartner) {
            return Handshake
          }
          const domainUrl = currentCompany.domainName
          return domainUrl ? `${TWENTY_ICONS_BASE_URL}/${domainUrl}` : Building2
        })(),
        plan: 'Company',
        subtitle: (() => {
          // Show "Partner" for partner companies
          if (currentCompanyPartner) {
            return 'Partner'
          }
          if (currentCompany.phone) {
            return formatUSPhoneForDisplay(currentCompany.phone)
          }
          const domainUrl = currentCompany.domainName
          return domainUrl || currentCompany.externalOrgId
        })(),
      }
    : null

  return (
    <CompanySwitcher
      fetchCompanies={fetchCompanies}
      loadInitialCompanies={true}
      placeholder="Search companies..."
      onCompanySelect={handleCompanySelect}
      selectedCompany={selectedCompany}
    />
  )
}
